import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import { LoginSEO } from '../hooks/useSEO'
import './AuthPages.css'

type Step = 'email' | 'code'

export default function LoginPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const { sendCode, verifyCode, error, clearError } = useAuthStore()
  const from = (location.state as { from?: { pathname: string } })?.from?.pathname || '/jobs'

  const [step, setStep] = useState<Step>('email')
  const [email, setEmail] = useState('')
  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [emailError, setEmailError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  const codeInputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    clearError()
  }, [clearError])

  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  const validateEmail = () => {
    if (!email) {
      setEmailError('Email is required')
      return false
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Please enter a valid email address')
      return false
    }
    setEmailError('')
    return true
  }

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateEmail()) return

    setIsLoading(true)
    try {
      await sendCode(email)
      setStep('code')
      setResendCooldown(60)
      // Focus first code input
      setTimeout(() => codeInputRefs.current[0]?.focus(), 100)
    } catch {
      // Error handled by store
    } finally {
      setIsLoading(false)
    }
  }

  const handleCodeChange = (index: number, value: string) => {
    if (!/^\d*$/.test(value)) return // Only allow digits

    const newCode = [...code]
    newCode[index] = value.slice(-1) // Only keep last digit
    setCode(newCode)

    // Auto-focus next input
    if (value && index < 5) {
      codeInputRefs.current[index + 1]?.focus()
    }

    // Auto-submit when all digits entered
    if (newCode.every(digit => digit) && newCode.join('').length === 6) {
      handleVerifyCode(newCode.join(''))
    }
  }

  const handleCodeKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      codeInputRefs.current[index - 1]?.focus()
    }
  }

  const handleCodePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pastedData.length === 6) {
      const newCode = pastedData.split('')
      setCode(newCode)
      handleVerifyCode(pastedData)
    }
  }

  const handleVerifyCode = async (fullCode: string) => {
    setIsLoading(true)
    try {
      await verifyCode(email, fullCode)
      navigate(from, { replace: true })
    } catch {
      // Error handled by store
      setCode(['', '', '', '', '', ''])
      codeInputRefs.current[0]?.focus()
    } finally {
      setIsLoading(false)
    }
  }

  const handleResendCode = async () => {
    if (resendCooldown > 0) return

    setIsLoading(true)
    try {
      await sendCode(email)
      setResendCooldown(60)
      setCode(['', '', '', '', '', ''])
    } catch {
      // Error handled by store
    } finally {
      setIsLoading(false)
    }
  }

  const handleBack = () => {
    setStep('email')
    setCode(['', '', '', '', '', ''])
    clearError()
  }

  return (
    <div className="auth-container">
      <LoginSEO />
      <div className="auth-card">
        <div className="auth-header">
          <Link to="/" className="auth-logo">
            <span className="logo-you">You</span>
            <span className="logo-claw">Claw</span>
          </Link>
          <h1>{step === 'email' ? 'Sign In' : 'Enter Code'}</h1>
          <p className="text-secondary">
            {step === 'email'
              ? 'Enter your email to receive a verification code'
              : `We sent a 6-digit code to ${email}`}
          </p>
        </div>

        {error && <div className="alert alert-error">{error}</div>}

        {step === 'email' ? (
          <form onSubmit={handleSendCode}>
            <div className="form-group">
              <label htmlFor="email" className="label">Email</label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value)
                  setEmailError('')
                }}
                className={`input ${emailError ? 'input-error' : ''}`}
                placeholder="you@example.com"
                disabled={isLoading}
                autoFocus
              />
              {emailError && <p className="form-error">{emailError}</p>}
            </div>

            <button type="submit" className="btn btn-primary btn-full" disabled={isLoading}>
              {isLoading ? (
                <>
                  <span className="spinner" style={{ width: 16, height: 16 }} />
                  Sending code...
                </>
              ) : (
                'Continue'
              )}
            </button>
          </form>
        ) : (
          <div>
            <div className="code-inputs">
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => { codeInputRefs.current[index] = el }}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleCodeChange(index, e.target.value)}
                  onKeyDown={(e) => handleCodeKeyDown(index, e)}
                  onPaste={handleCodePaste}
                  className="code-input"
                  disabled={isLoading}
                  autoFocus={index === 0}
                />
              ))}
            </div>

            <div className="code-actions">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleBack}
                disabled={isLoading}
              >
                Change email
              </button>
              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleResendCode}
                disabled={isLoading || resendCooldown > 0}
              >
                {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : 'Resend code'}
              </button>
            </div>

            {isLoading && (
              <div className="verifying-message">
                <span className="spinner" style={{ width: 16, height: 16 }} />
                Verifying...
              </div>
            )}
          </div>
        )}

        <div className="auth-footer">
          <p className="text-secondary text-small">
            By continuing, you agree to our Terms of Service
          </p>
        </div>
      </div>
    </div>
  )
}
